import { type PositionStyle, generatePositionStyle } from '@/utils/positioning';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { getTextAlignStyleVariables } from '../utils/typography';
import { useAnimatedBlock } from '../utils/useAnimatedBlock';
import { PARAGRAPH_DEFAULTS } from './Paragraph.constants';
import styles from './Paragraph.css';
import type { ParagraphElementProps } from './Paragraph.types';

// Keeping this as inherit could have unintended consequences with older templates so let's force to be transparent on paragraph elements
const getBackgroundColor = (backgroundColor: string) => {
  if (backgroundColor === 'inherit') return 'transparent';
  return backgroundColor;
};

export function ParagraphElement(props: ParagraphElementProps) {
  const { element, children, attributes, className, as, ...rest } = props;
  const {
    width,
    maxWidth,
    lineHeight,
    mobileLineHeight,
    textAlign,
    mobileTextAlign,
    mobile_hide,
    desktop_hide,
    animation,
    padding,
    mobilePadding,
    margin,
    customAttributes,
    backgroundColor,
    lineClampClass,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    textTransform,
    letterSpacing,
  } = element.attrs || {};

  const [marginTop, marginRight, marginBottom, marginLeft] = (margin ?? PARAGRAPH_DEFAULTS.margin)
    .toString()
    .split(' ');
  const [ref, animatedClassname] = useAnimatedBlock(animation);

  const Tag = as || 'p';

  return (
    <Tag
      {...rest}
      ref={ref}
      {...customAttributes}
      className={classNames(
        className,
        {
          'mobile:hidden': mobile_hide,
          'desktop:hidden': desktop_hide,
        },
        animatedClassname,
        styles.paragraphElement,
        customAttributes?.className,
        lineClampClass,
      )}
      style={
        {
          ...(element?.attrs && generatePositionStyle(element.attrs as PositionStyle)),
          width,
          maxWidth,
          '--padding': padding ?? PARAGRAPH_DEFAULTS.padding,
          '--mobile-padding': mobilePadding ?? PARAGRAPH_DEFAULTS.padding,
          '--padding-root': padding ?? PARAGRAPH_DEFAULTS.paddingRoot,
          '--margin-top': marginTop,
          '--margin-right': marginRight,
          '--margin-bottom': marginBottom,
          '--margin-left': marginLeft,
          '--margin': margin ?? PARAGRAPH_DEFAULTS.margin,
          '--line-height': lineHeight,
          '--mobile-line-height': mobileLineHeight,
          '--background-color': backgroundColor ? getBackgroundColor(backgroundColor) : 'transparent',
          '--border-radius': borderRadius,
          '--border-width': borderWidth,
          '--border-color': borderColor,
          '--border-style': borderStyle,
          '--text-transform': textTransform,
          '--letter-spacing': letterSpacing ?? PARAGRAPH_DEFAULTS.letterSpacing,
          ...getTextAlignStyleVariables({
            mobileAlign: mobileTextAlign || textAlign,
            desktopAlign: textAlign,
          }),
        } as CSSProperties
      }
      {...attributes}
    >
      {children}
    </Tag>
  );
}
